import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, Search, Coins, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import { ThemeToggle } from '@/components/stashstr/ThemeToggle';
import { XmrTicker } from '@/components/stashstr/XmrTicker';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Wordmark } from '@/components/stashstr/Wordmark';
import { NAV_LINK_BASE, NAV_LINK_ACTIVE_FULL } from '@/lib/navTheme';

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useCurrentUser();

  return (
    <header className="stashstr-nav sticky top-0 z-50 bg-white dark:bg-[#111214] border-b border-gray-200 dark:border-[#2a2d30]">
      {/* Edge-to-edge wrapper: groups pin to viewport edges on narrow screens,
          container-aligned on wide screens (stasher.news geometry). */}
      <div className="px-3">
        <div className="max-w-3xl mx-auto h-12 flex items-center gap-3">
          {/* Left: wordmark + frontpage/new/top/search */}
          <div className="flex items-center gap-3 shrink-0 flex-1 justify-start">
            <Wordmark />
            <nav className="hidden sm:flex items-center gap-3 text-[13px]" aria-label="Primary">
              <NavLink
                to="/"
                end
                className={({ isActive }) => (isActive ? NAV_LINK_ACTIVE_FULL : NAV_LINK_BASE)}
              >
                frontpage
              </NavLink>
              <NavLink
                to="/new"
                className={({ isActive }) => (isActive ? NAV_LINK_ACTIVE_FULL : NAV_LINK_BASE)}
              >
                new
              </NavLink>
              <NavLink
                to="/top"
                className={({ isActive }) => (isActive ? NAV_LINK_ACTIVE_FULL : NAV_LINK_BASE)}
              >
                top
              </NavLink>
              <Link
                to="/search"
                aria-label="Search"
                className="p-1 rounded text-[rgba(0,0,0,0.55)] dark:text-[rgba(255,255,255,0.55)] hover:text-[rgba(0,0,0,0.75)] dark:hover:text-[rgba(255,255,255,0.75)] hover:bg-gray-100 dark:hover:bg-[#232528]"
              >
                <Search className="size-4" />
              </Link>
            </nav>
          </div>

          {/* Center: live XMR price (desktop) — flex-1 third for true centering */}
          <div className="hidden md:flex items-center justify-center flex-1">
            <XmrTicker />
          </div>

          {/* Right: theme, post, coins, login */}
          <div className="flex items-center gap-2 shrink-0 flex-1 justify-end">
            <ThemeToggle />
            {user && (
              <Link
                to="/settings/payments"
                aria-label="Payment addresses"
                className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-[#232528]"
              >
                <Coins className="size-4" />
              </Link>
            )}
            <Button
              asChild
              className="h-7 px-3 text-[13px] bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white rounded"
            >
              <Link to="/post">post</Link>
            </Button>
            <LoginArea className="max-w-40" />
            <button
              type="button"
              className="md:hidden p-1.5 rounded text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-[#232528]"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <nav
          className="md:hidden border-t border-gray-200 dark:border-[#2a2d30] bg-white dark:bg-[#111214] px-3 py-2 flex flex-col gap-1"
          aria-label="Mobile menu"
        >
          {[
            { to: '/', label: 'frontpage' },
            { to: '/new', label: 'new' },
            { to: '/top', label: 'top' },
            { to: '/post', label: 'post' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className="px-2 py-2 rounded text-[14px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#232528]"
            >
              {l.label}
            </Link>
          ))}
          {/* Compact live XMR price in the mobile menu */}
          <div className="px-2 py-2 border-t border-gray-200 dark:border-[#2a2d30]">
            <XmrTicker compact />
          </div>
        </nav>
      )}
    </header>
  );
}
